import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase, User, School, UserRole, Student } from '../lib/supabase';
import { saveAccount, updateLastUsed, getAccountKey } from '../components/shared/AccountSwitcher'
import { isRegistering } from '../lib/registrationGuard';

interface AuthState {
  user: User | null;
  school: School | null;
  linkedStudents: Student[];
  loading: boolean;
  initialized: boolean;
}

interface AuthContextType extends AuthState {
  signIn: (identifier: string, password: string, role?: UserRole) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshSchool: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Cleans up a phone number that ParentLoginPage.tsx already built as
// "+<countryCode><digits>" via its country-code dropdown + number field
// (e.g. "+14155551234"). No longer guesses/strips Pakistan-specific
// prefixes like the old version did — the country code is now explicit
// from the dropdown, so this just strips stray formatting characters and
// makes sure there's a single leading +. Must stay byte-for-byte identical
// to ParentSignupPage.tsx's version of this function.
//
// NOTE: ParentSignupPage.tsx has NOT been updated to match yet (still
// Pakistan-only) — so today, only +92 numbers will actually have a matching
// account to log into. This function is ready for other country codes as
// soon as signup is updated too.
function normalizePhone(raw: string | null | undefined): string {
  if (!raw) return '';
  let cleaned = raw.replace(/[^\d+]/g, '');
  cleaned = cleaned.replace(/(?!^)\+/g, ''); // keep only a single leading +
  if (!cleaned.startsWith('+')) cleaned = `+${cleaned}`;
  const digitsOnly = cleaned.slice(1);
  return digitsOnly ? cleaned : '';
}

// ─── Shared restriction check ──────────────────────────────────
// The ONE place this logic lives. Used by three call sites that all need
// it and previously only one of them (signIn()) had it:
//   1. The SIGNED_IN listener below — the real backstop, since it's what
//      actually sets `state.user`, and it fires for ANY sign-in on the
//      shared `supabase` client (signIn() AND AccountSwitcher's
//      handleSwitch(), since both use that same client instance).
//      PublicRoute/ProtectedRoute (App.tsx) react to `state.user` the
//      instant it changes — without the check living here too, a
//      restricted student's profile could get set as `state.user` (and
//      PublicRoute could already have redirected them into their
//      dashboard) before signIn()'s own slower check finishes and signs
//      them back out.
//   2. signIn() — kept so the login form gets an immediate, specific
//      error message instead of just watching the user silently vanish
//      a moment later.
//   3. AccountSwitcher.tsx's handleSwitch() — checked before it navigates
//      via window.location.href, so a restricted account never reaches
//      that line at all.
//
// Deliberately three FLAT queries, not embedded selects (`schools(...)`/
// `classes(...)` off a parent table) — an earlier version used embeds and
// the whole check silently failed open in testing (a restricted student
// logged in fine), almost certainly because a swallowed embed/RLS error
// left a chained variable undefined and `?.` just skipped the block
// instead of blocking. Every query below logs its error explicitly
// instead of ignoring it, so if this ever fails again it fails loud in
// the console rather than silently open.
// `client` defaults to the shared `supabase` client (matches signIn() and
// the SIGNED_IN listener, which both call this AFTER the account being
// checked is already the shared client's active session — so these
// queries run as that account reading its own rows). AccountSwitcher's
// handleSwitch() is the one caller where that assumption used to break:
// it verifies the SWITCH TARGET's credentials on a separate, isolated
// `validationClient` (so the CURRENTLY logged-in account's session is
// never disturbed), which means the shared client's session at that point
// is still the current account, not the target. Querying via `supabase`
// there would ask "can the current account read the target's rows"
// instead of "can the target read its own" — a different, likely-stricter
// RLS question with no guarantee of the same answer. Passing the same
// `validationClient` in as `client` here keeps every caller's check
// running as genuine self-access, regardless of which client did the
// authenticating.
type RestrictionClient = Pick<typeof supabase, 'from'>;

export async function checkStudentRestriction(userId: string, client: RestrictionClient = supabase): Promise<string | null> {
  const { data: profileCheck, error: profileErr } = await client
    .from('users')
    .select('role, school_id')
    .eq('id', userId)
    .single();
  if (profileErr) {
    console.error('SIGNIN_RESTRICTION_PROFILE', profileErr.message);
    return "Couldn't verify this account's status. Please try again.";
  }

  if (profileCheck?.role !== 'student' || !profileCheck.school_id) return null;

  const { data: schoolCheck, error: schoolErr } = await client
    .from('schools')
    .select('account_restriction_cutoff')
    .eq('id', profileCheck.school_id)
    .single();
  if (schoolErr) {
    console.error('SIGNIN_RESTRICTION_SCHOOL', schoolErr.message);
    return "Couldn't verify this account's status. Please try again.";
  }

  const cutoff = schoolCheck?.account_restriction_cutoff;
  if (cutoff == null) return null;

  const { data: studentRow, error: studentErr } = await client
    .from('students')
    .select('class_id')
    .eq('user_id', userId)
    .maybeSingle();
  if (studentErr) {
    console.error('SIGNIN_RESTRICTION_STUDENT', studentErr.message);
    return "Couldn't verify this account's status. Please try again.";
  }

  if (!studentRow?.class_id) return null;

  const { data: classRow, error: classErr } = await client
    .from('classes')
    .select('grade_level')
    .eq('id', studentRow.class_id)
    .single();
  if (classErr) {
    console.error('SIGNIN_RESTRICTION_CLASS', classErr.message);
    return "Couldn't verify this account's status. Please try again.";
  }

  const gradeLevel = classRow?.grade_level ?? null;
  if (gradeLevel != null && gradeLevel < cutoff) {
    return `This account can't log in — Class ${gradeLevel} is restricted. Contact your school admin.`;
  }
  return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    school: null,
    linkedStudents: [],
    loading: true,
    initialized: false,
  });
  const isSigningOut = useRef(false);

  const fetchUserProfile = useCallback(async (authUserId: string): Promise<User | null> => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', authUserId)
      .maybeSingle();
    if (error) {
      console.error('fetchUserProfile error:', error);
      return null;
    }
    return data as User;
  }, []);

  const fetchSchool = useCallback(async (schoolId: string): Promise<School | null> => {
    const { data, error } = await supabase
      .from('schools')
      .select('*')
      .eq('id', schoolId)
      .maybeSingle();
    if (error) {
      console.error('fetchSchool error:', error);
      return null;
    }
    return data as School;
  }, []);

  const fetchLinkedStudents = useCallback(async (parentUserId: string): Promise<Student[]> => {
    const { data, error } = await supabase
      .from('parent_students')
      .select('student_id, students(*)')
      .eq('parent_id', parentUserId);
    if (error) return [];
    return (data || []).map((row: any) => row.students).filter(Boolean) as Student[];
  }, []);

  const initializeSession = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      console.log('initializeSession - session:', session?.user?.email);
      if (!session?.user) {
        setState({ user: null, school: null, linkedStudents: [], loading: false, initialized: true });
        return;
      }
      const profilePromise = fetchUserProfile(session.user.id);
      const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000));
      const userProfile = await Promise.race([profilePromise, timeoutPromise]);
      console.log('initializeSession - userProfile:', userProfile);
      if (!userProfile) {
        // Mid-registration, the Auth account exists a moment before its profile
        // row does. Signing out here would destroy the session we need in order
        // to write that row — and the account would be stranded: email taken,
        // no profile, unable to log in. So leave the session alone and just
        // report "no user" to the app. See lib/registrationGuard.ts.
        if (isRegistering()) {
          console.log('initializeSession - no profile yet, but registration in progress; keeping session');
          setState({ user: null, school: null, linkedStudents: [], loading: false, initialized: true });
          return;
        }
        console.log('initializeSession - NO PROFILE, signing out');
        await supabase.auth.signOut();
        setState({ user: null, school: null, linkedStudents: [], loading: false, initialized: true });
        return;
      }
      
      let school: School | null = null;
      if (userProfile.school_id) {
        school = await fetchSchool(userProfile.school_id);
        console.log('initializeSession - school:', school);
      }
      let linkedStudents: Student[] = [];
      if (userProfile.role === 'parent') {
        linkedStudents = await fetchLinkedStudents(userProfile.id);
      }

      // Update last_used for this account in the switcher
      updateLastUsed(getAccountKey(userProfile));

      // Same tracking as the SIGNED_IN branch below, but for the "already
      // logged in, just reloaded the page" path — which is the common case
      // and was previously missed entirely. The edge function itself
      // dedupes (touches last_active_at on a matching existing row rather
      // than inserting a new one every refresh).
      if (userProfile.role === 'super_admin') {
        supabase.functions.invoke('track-admin-session', {
          body: { admin_id: userProfile.id, user_agent: navigator.userAgent },
        }).catch(() => { /* best-effort only */ });
      }

      setState({ user: userProfile, school, linkedStudents, loading: false, initialized: true });
    } catch {
      setState({ user: null, school: null, linkedStudents: [], loading: false, initialized: true });
    }
  }, [fetchUserProfile, fetchSchool]);

  useEffect(() => {
    initializeSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (isSigningOut.current) return;

      if (event === 'SIGNED_OUT' || !session) {
        setState({ user: null, school: null, linkedStudents: [], loading: false, initialized: true });
        return;
      }

      // TOKEN_REFRESHED: don't re-fetch from DB, just keep existing state
      if (event === 'TOKEN_REFRESHED') {
        return;
      }

      // SIGNED_IN: fetch profile outside the auth lock
      if (event === 'SIGNED_IN') {
        setTimeout(async () => {
          console.log('SIGNED_IN fired for:', session.user.id);
          const userProfile = await fetchUserProfile(session.user.id);
          console.log('userProfile fetched:', userProfile);
          if (!userProfile) {
            console.log('NO PROFILE - signing out');
            setState({ user: null, school: null, linkedStudents: [], loading: false, initialized: true });
            return;
          }

          // Restriction backstop — see checkStudentRestriction's comment
          // above for why this has to live here, not just in signIn().
          const restrictionError = await checkStudentRestriction(userProfile.id);
          if (restrictionError) {
            console.error('SIGNIN_RESTRICTION_BLOCKED (SIGNED_IN listener):', restrictionError);
            await supabase.auth.signOut();
            setState({ user: null, school: null, linkedStudents: [], loading: false, initialized: true });
            return;
          }

          let school: School | null = null;
          if (userProfile.school_id) {
            school = await fetchSchool(userProfile.school_id);
            console.log('school fetched:', school);
          }
          let linkedStudents: Student[] = [];
          if (userProfile.role === 'parent') {
            linkedStudents = await fetchLinkedStudents(userProfile.id);
          }
          setState({ user: userProfile, school, linkedStudents, loading: false, initialized: true });

          // Device/session tracking is admin-only (see AdminSessions.tsx) —
          // fire-and-forget, never blocks login if the edge function is
          // slow or down.
          if (userProfile.role === 'super_admin') {
            supabase.functions.invoke('track-admin-session', {
              body: { admin_id: userProfile.id, user_agent: navigator.userAgent },
            }).catch(() => { /* best-effort only */ });
          }
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, [initializeSession, fetchUserProfile, fetchSchool]);

  // Keeps state.school live against DB changes instead of only ever being
  // fetched once at session init / SIGNED_IN. Without this, an admin flipping
  // subscription_status mid-session (e.g. the daily lock cron, or Set Fee's
  // immediate no-trial invoice path) left every route guard that reads
  // school.subscription_status (App.tsx's pending_payment/suspended redirects)
  // working off stale data until the person manually logged out and back in —
  // this is what let a Principal keep browsing normally after their school
  // was locked in the DB. Requires the `schools` table to have realtime
  // replication enabled in Supabase (Database > Replication, or
  // `alter publication supabase_realtime add table schools;`) — confirm this
  // is actually turned on, since a silently-missing subscription looks
  // identical to this fix simply not being deployed.
  useEffect(() => {
    const schoolId = state.school?.id;
    if (!schoolId) return;

    const channel = supabase
      .channel(`school-status-${schoolId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'schools', filter: `id=eq.${schoolId}` },
        (payload) => {
          setState(s => (s.school?.id === schoolId ? { ...s, school: payload.new as School } : s));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [state.school?.id]);

  const signIn = async (identifier: string, password: string, role?: UserRole): Promise<{ error: string | null }> => {
    // Deliberately does NOT touch the global `loading` flag. PublicRoute/
    // ProtectedRoute (App.tsx) unmount whatever they're showing and render
    // a bare LoadingScreen whenever `loading` is true — that's meant for
    // the one-time "figuring out if there's an existing session" phase on
    // app boot, not for an in-flight signIn() call. Toggling it here was
    // tearing down LoginPage mid-submit and remounting a fresh instance
    // once signIn() resolved, which silently wiped out whatever error
    // setError() had just set on the old (now-gone) instance — this is
    // why a blocked login looked like "just bounces back to a blank login
    // page" instead of showing the restriction message. LoginPage.tsx
    // already tracks its own local `loading` state for the submit button,
    // so the global flag isn't needed here at all.
    try {
      const isParent = role === 'parent';
      let data: any, error: any;

      if (isParent) {
        // Parents auth via email derived from phone (matches signup identity).
        // `identifier` arrives here already built as "+<countryCode><digits>"
        // by ParentLoginPage.tsx's country-code dropdown + number field.
        const phoneNormalized = normalizePhone(identifier);
        const parentEmail = `${phoneNormalized.replace(/\+/g, '')}@edtrack.internal`;
        ({ data, error } = await supabase.auth.signInWithPassword({
          email: parentEmail,
          password,
        }));
      } else {
        const email = `${identifier.trim()}@edtrack.internal`;
        console.log('signIn attempting:', email);
        ({ data, error } = await supabase.auth.signInWithPassword({ email, password }));
      }

      console.log('signIn result - data:', data?.user?.email ?? data?.user?.phone, 'error:', error?.message);
if (error) {
  if (error.message.includes('Invalid login') || error.message.includes('invalid_grant')) {
    // Check if the user exists at all
    const { data: userExists } = await supabase
  .from('users')
  .select('id, role, username')
  .ilike('username', identifier.trim())
  .maybeSingle();

    // NOTE: this lookup runs while still signed out, and `users` SELECT is now
    // restricted to authenticated callers — so it always comes back empty and
    // can't distinguish "no such username" from "wrong password". Worded
    // accordingly instead of confidently claiming the account doesn't exist,
    // which is what it used to do for every single failed login.
    if (!userExists) {
      return { error: `Couldn't sign in as "${identifier}". Check the username and password, or contact your principal.` };
    }

    if (role && userExists.role !== role) {
      return { error: `Username "${identifier}" exists but is registered as a ${userExists.role}, not ${role}. Please select the correct role.` };
    }

    return { error: 'Password is incorrect. Contact your principal if you forgot it.' };
  }

  if (error.message.includes('Email not confirmed')) {
    return { error: 'Account not confirmed. Contact your school admin.' };
  }

  if (error.message.includes('Too many requests')) {
    return { error: 'Too many login attempts. Please wait a few minutes and try again.' };
  }

  return { error: `Login failed: ${error.message}` };
}
      if (!data.user) {
        return { error: 'Login failed. Please try again.' };
      }

      // Account-restriction + role validation. Restriction used to be a
      // stored `login_suspended` flag, kept in sync by a periodic sweep
      // (reconcile_account_restrictions, see lib/accountRestriction.ts) —
      // but that could drift out of sync with reality (confirmed live: a
      // genuinely-restricted account could still sign in, and vice versa).
      // Replaced with a LIVE check computed fresh on every sign-in attempt,
      // straight from the student's CURRENT class and the school's CURRENT
      // cutoff — nothing stored, nothing that can go stale. Only students
      // can be restricted; every student now always has a real login
      // (create_student_user always runs — see PrincipalStudents.tsx /
      // TeacherStudents.tsx), so this is purely a sign-in-time gate, not an
      // account-existence gate the way it used to be.
      //
      // The restriction check itself now lives in checkStudentRestriction()
      // (module scope above) — shared with the SIGNED_IN listener (the real
      // backstop) and AccountSwitcher's handleSwitch(). Kept here too so
      // the login form gets an immediate, specific error message.
      //
      // IMPORTANT: this branch deliberately does NOT call
      // supabase.auth.signOut() itself. The SIGNED_IN listener is running
      // its OWN copy of this same check concurrently (it fires off the
      // same signInWithPassword() call above) — if this branch signed out
      // first, it would yank the session out from under the listener's
      // still-in-flight queries, those queries would then error out, and
      // (before this was fixed to fail closed) that got silently read as
      // "not restricted" — logging the student in anyway, seconds after
      // this exact message was shown. The listener is the single place
      // that actually decides state.user and is the only one that should
      // call signOut() for a restriction; this branch only needs to report
      // the message to the login form.
      {
        const restrictionError = await checkStudentRestriction(data.user.id);
        if (restrictionError) {
          return { error: restrictionError };
        }

        const { data: profileCheck, error: profileErr } = await supabase
          .from('users')
          .select('role')
          .eq('id', data.user.id)
          .single();
        if (profileErr) console.error('SIGNIN_ROLE_CHECK', profileErr.message);

        if (role && profileCheck && profileCheck.role !== role) {
          await supabase.auth.signOut();
          return { error: "These credentials don't match this role. Please select the correct role." };
        }
      }

      // Save account for switcher — awaited directly (not deferred via
      // setTimeout) so this is guaranteed to finish before signIn()
      // returns and the caller navigates anywhere. The previous
      // setTimeout(..., 500) version was fire-and-forget: if a full page
      // reload happened within that 500ms window (a redirect, a dev
      // server restart, closing the tab), the save was silently dropped
      // and the account ended up with no password on file at all.
      try {
        const { data: userProfile } = await supabase.from('users').select('full_name, school_id, role, username').eq('id', data.user!.id).single();
        if (userProfile) {
          let schoolName = 'Unknown School';
          if (userProfile.school_id) {
            const { data: schoolData } = await supabase.from('schools').select('name').eq('id', userProfile.school_id).single();
            if (schoolData) schoolName = schoolData.name;
          }
          saveAccount({
            // Use `identifier` (what was actually typed/authenticated
            // with in this signIn call) rather than the DB's
            // users.username field. The two can legitimately differ
            // (parents authenticate by phone, but users.username for
            // a parent is a separate generated string) — saving under
            // users.username here while AccountSwitcher's own "Add
            // Account" flow saves under the typed identifier caused
            // the same person to end up stored twice under two
            // different keys. saveAccount() normalizes casing/
            // whitespace on top of this, so both paths now converge.
            username: identifier,
            password,
            school_id: userProfile.school_id || '',
            school_name: schoolName,
            user_full_name: userProfile.full_name,
            role: userProfile.role,
            last_used: new Date().toISOString(),
          });
        }
      } catch {
        // non-critical
      }

      console.log('signIn success - waiting for onAuthStateChange');
      return { error: null };
    } catch {
      return { error: 'An unexpected error occurred.' };
    }
  };

  const signOut = async () => {
    isSigningOut.current = true;
    // Clear this school's cached student roster (see TeacherStudents.tsx's
    // `edtrack_students_${schoolId}` cache) — a full sign-out, unlike switching
    // accounts via AccountSwitcher, shouldn't leave another account's school
    // roster sitting in localStorage on a shared device.
    if (state.school?.id) {
      try { localStorage.removeItem(`edtrack_students_${state.school.id}`); } catch {}
    }
    await supabase.auth.signOut();
    setState({ user: null, school: null, linkedStudents: [], loading: false, initialized: true });
    isSigningOut.current = false;
    window.location.href = '/';
  };

  const refreshUser = async () => {
    if (!state.user) return;
    const userProfile = await fetchUserProfile(state.user.id);
    if (userProfile) setState(s => ({ ...s, user: userProfile }));
  };

  const refreshSchool = async () => {
    if (!state.user?.school_id) return;
    const school = await fetchSchool(state.user.school_id);
    if (school) setState(s => ({ ...s, school }));
  };

  return (
    <AuthContext.Provider value={{ ...state, signIn, signOut, refreshUser, refreshSchool }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function getRoleBasePath(role: UserRole): string {
  switch (role) {
    case 'super_admin': return '/admin';
    case 'principal':   return '/principal';
    case 'teacher':     return '/teacher';
    case 'parent':      return '/parent';
    case 'student':     return '/student';
    default:            return '/login';
  }
}